import { useState, useEffect } from 'react';
import { usePutProfiles } from '@/api/control/control-plane/control-plane';
import type { ProfileDto } from '@/api/control/missionImpactAPI.schemas';
import { useRoles } from '@/contexts/RolesContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Settings, AlertTriangle } from 'lucide-react';
import { useError } from '@/contexts/ErrorContext';
import { toast } from 'sonner';

/**
 * Props for the SyncSettingsDialog component
 */
interface SyncSettingsDialogProps {
  /** Organization ID */
  orgId: string;
  /** The profile to update */
  profile: ProfileDto;
  /** Callback when settings are successfully updated */
  onSuccess: () => void;
  /** Optional external control of dialog open state */
  externalOpen?: boolean;
  /** Optional callback when external open state should change */
  onExternalOpenChange?: (open: boolean) => void;
}

/**
 * Dialog for configuring sync settings including delete mode
 */
export function SyncSettingsDialog({ orgId, profile, onSuccess, externalOpen, onExternalOpenChange }: SyncSettingsDialogProps) {
  const { isAdminOrOperator } = useRoles();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen ?? internalOpen;
  const setOpen = onExternalOpenChange ?? setInternalOpen;
  const [selectedMode, setSelectedMode] = useState(profile.deleteMode);
  const [acknowledged, setAcknowledged] = useState(false);

  const hasChanged = selectedMode !== profile.deleteMode;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedMode(profile.deleteMode);
      setAcknowledged(false);
    }
  }, [open, profile.deleteMode]);

  const { mutate: updateProfile, isPending: isUpdating } = usePutProfiles({
    mutation: {
      onSuccess: () => {
        toast.success(t.profiles.syncSettings.updateSuccess);
        setOpen(false);
        onSuccess();
      },
      onError: (error) => {
        handleApiError(error, { context: 'SyncSettingsDialog.updateProfile', profile });
      },
    },
  });

  /**
   * Handles saving the settings
   */
  const handleSave = () => {
    updateProfile({
      orgId,
      profileId: profile.id,
      data: {
        name: profile.name,
        regionId: profile.regionId,
        deleteMode: selectedMode as ProfileDto['deleteMode'],
      },
    });
  };

  const canSave = !hasChanged || acknowledged;

  // Hide built-in trigger when in controlled mode (externalOpen is provided)
  const isControlled = externalOpen !== undefined;

  if (!isAdminOrOperator) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" className="h-9">
            <Settings className="mr-2 h-4 w-4" />
            {t.profiles.syncSettings.buttonText}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.profiles.syncSettings.title}</DialogTitle>
          <DialogDescription>{t.profiles.syncSettings.description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="deleteMode">{t.profiles.syncSettings.deleteModeLabel}</Label>
            <Select value={selectedMode} onValueChange={(value) => { setSelectedMode(value as typeof selectedMode); }}>
              <SelectTrigger id="deleteMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Soft" className="h-auto py-2">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">{t.wizard.deleteMode.softDelete || 'Soft Delete'}</span>
                    <span className="text-xs text-muted-foreground whitespace-normal [[data-slot=select-value]_&]:hidden">
                      {t.wizard.deleteMode.softDeleteDescription}
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="HardTombstone" className="h-auto py-2">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">{t.wizard.deleteMode.hardDelete || 'Hard Delete'}</span>
                    <span className="text-xs text-muted-foreground whitespace-normal [[data-slot=select-value]_&]:hidden">
                      {t.wizard.deleteMode.hardDeleteDescription}
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t.wizard.deleteMode.infoMessage}
            </p>
          </div>

          {hasChanged && (
            <div className="space-y-3">
              <div className="bg-destructive/10 border-destructive/20 border rounded-md p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-destructive font-medium">
                      {t.profiles.syncSettings.warningTitle}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t.profiles.syncSettings.warningDescription}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acknowledge-change"
                  checked={acknowledged}
                  onCheckedChange={(checked) => { setAcknowledged(checked === true); }}
                  className="mt-1"
                />
                <label
                  htmlFor="acknowledge-change"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {t.profiles.syncSettings.acknowledgeChange}
                </label>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); }} disabled={isUpdating}>
            {t.profiles.syncSettings.cancel}
          </Button>
          <Button onClick={handleSave} disabled={isUpdating || !canSave}>
            {isUpdating ? t.profiles.syncSettings.saving : t.profiles.syncSettings.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
